import { Box, Typography } from "@mui/material"
import { CheckCircleFilledRounded } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import type { PurchaseCostFormValues } from "./purchaseCostFormSchema"

import { ShippingTermsFieldGroup } from "@/components/Forms"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

const SECTION_HEADING_CLASSES = "mb-4 flex items-center leading-6 tracking-[0.15px] text-gray-700"

export const PurchaseCostStep2Form: FC = () => {
  const { getValues } = useFormContext<PurchaseCostFormValues>()

  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const uomLabelByUomId = useMemo(() => {
    const map: Record<string, string> = {}
    units.forEach((unit) => {
      map[unit.id] = unit.name ?? unit.description ?? unit.id
    })
    return map
  }, [units])

  const { tiers, effectiveDate, expirationDate } = getValues()

  return (
    <Box className='flex flex-col gap-y-6'>
      <Box>
        <Typography variant='body1' fontWeight={700} className={SECTION_HEADING_CLASSES}>
          1. Price Breakdown
          <CheckCircleFilledRounded className='ml-2 size-[18px] text-success-500' />
        </Typography>
        <Box className='px-6'>
          <Box className='mb-3 w-1/2'>
            <Box className='flex justify-between'>
              <Typography variant='body2' className='font-bold text-gray-700'>
                Quantity
              </Typography>
              <Typography variant='body2' className='w-1/2 font-bold text-gray-700'>
                Price/UoM
              </Typography>
            </Box>
            {tiers.map((tier, index) => (
              <Box key={index} className='flex justify-between'>
                <Typography variant='body2' className='text-gray-700'>
                  {tier.qty ?? ""}
                </Typography>
                <Typography variant='body2' className='w-1/2 text-gray-700'>
                  {tier.price !== null ? `$${tier.price} / ${uomLabelByUomId[tier.uomId] ?? ""}` : ""}
                </Typography>
              </Box>
            ))}
          </Box>
          <p className='mb-3 py-0 text-sm text-gray-900'>
            Effective Date: {effectiveDate ? dayjs(effectiveDate).format("MM/DD/YYYY") : "-"}
          </p>
          <p className='py-0 text-sm text-gray-900'>
            Expiration Date: {expirationDate ? dayjs(expirationDate).format("MM/DD/YYYY") : "-"}
          </p>
        </Box>
      </Box>

      <Box>
        <Typography variant='body1' fontWeight={700} className={SECTION_HEADING_CLASSES}>
          2. Shipping Terms
        </Typography>
        <ShippingTermsFieldGroup testIdPrefix='product.purchase-costs.form-dialog' />
      </Box>
    </Box>
  )
}
